<template>
  <v-container fluid class="px-12 py-8" v-if="functionInputList">
    <breadcrumbs></breadcrumbs>
    <h1>{{ id ? "Edit" : "Create" }} Security Function</h1>
    <v-row justify="center" v-if="showForm">
      <v-col cols="12">
        <v-form @submit.prevent="onSubmit" v-model="valid" class="mt-5">
          <label class="och-label"> Name </label>
          <v-text-field v-model="name" outlined counter="255"></v-text-field>
        </v-form>

        <div class="text-right mt-5">
          <v-btn v-if="id" @click="resetForm" outlined class="mr-2"
            >Cancel</v-btn
          >
          <v-btn
            :disabled="!valid"
            @click="save"
            color="primary"
            class="elevation-0"
            >Save</v-btn
          >
        </div>
      </v-col>
    </v-row>
    <div v-else>
      <v-divider class="mt-5 mb-3"></v-divider>
      <v-row>
        <v-col cols="9">
          <label class="och-label caption">Security Function Name</label>
          <div class="headline">{{ record.name }}</div>
        </v-col>
        <v-col cols="3" class="text-right mt-2">
          <v-btn color="primary" outlined @click="edit">Edit</v-btn>
        </v-col>
      </v-row>
      <v-divider class="mt-3 mb-5"></v-divider>

      <function-input-table></function-input-table>

      <div class="title mt-10">
        Filtered Templates ({{ filteredTemplateList.length }})
      </div>
      <ul class="mt-3">
        <li v-for="item in filteredTemplateList" :key="item.id">
          {{ item.name }} ({{ item.id }})
        </li>
      </ul>
      <div v-if="!filteredTemplateList.length">
        <i>No templates match the associated security points</i>
      </div>
    </div>
  </v-container>
</template>
<script>
import { mapActions, mapGetters, mapState } from "vuex";
import Breadcrumbs from "../Breadcrumbs";
import FunctionInputTable from "./input/Table";

export default {
  components: {
    Breadcrumbs,
    FunctionInputTable,
  },
  computed: {
    filteredTemplateList() {
      const functionInputList = this.filterFunctionInputList(
        "secFunctionId",
        this.id
      );

      /*
        Find all classes that match the item value and pass value matching. However,
        the implementation here is a little confusing. The .find() of the 
        functionInputList returns "true" so that it can return early, and its value
        is inverted to the .filter() so that it is not included in the filtered list.
        This is an optimization so that the callback function is not called if the first
        test of matching fails.
      */
      const matchedClassList = this.securityClassList.filter(securityClass => {
        const { itemList } = securityClass;
        return !functionInputList.find(input => {
          // Checks if item matches and returns early if not.
          const matchedItem = itemList.find(item => item.item === input.point);
          if (!matchedItem) return true;

          const { callback } = this.filterOperatorList(
            "name",
            input.operator
          )[0];

          const { valueList } = matchedItem;
          if (!callback(valueList, input.value)) return true;
        });
      });

      const matchedClassIdList = matchedClassList.map(item => item.id);
      return this.templateList.filter(
        item =>
          matchedClassIdList.includes(item.epiccareSecurityClass) ||
          matchedClassIdList.includes(item.cadenceSecurityClass) ||
          matchedClassIdList.includes(item.sharedSecurityClass) ||
          matchedClassIdList.includes(item.epiccareSecurityClass) ||
          matchedClassIdList.includes(item.cadenceSecurityClass) ||
          matchedClassIdList.includes(item.sharedSecurityClass) ||
          matchedClassIdList.includes(item.professionalBillingSecurityClass) ||
          matchedClassIdList.includes(item.careEverywhereSecurityClass) ||
          matchedClassIdList.includes(item.radarSecurityClass) ||
          matchedClassIdList.includes(item.bridgesSecurityClass) ||
          matchedClassIdList.includes(item.reportingWorkbenchSecurityClass) ||
          matchedClassIdList.includes(item.roiSecurityClass) ||
          matchedClassIdList.includes(item.identitySecurityClass) ||
          matchedClassIdList.includes(item.inBasketSecurityClass) ||
          matchedClassIdList.includes(item.myChartSecurityClass) ||
          matchedClassIdList.includes(item.preludeSecurityClass) ||
          matchedClassIdList.includes(item.referralSecurityClass) ||
          matchedClassIdList.includes(item.webSuiteSecurityClass) ||
          matchedClassIdList.includes(item.willowSecurityClass) ||
          matchedClassIdList.includes(item.analyticsSecurityClass) ||
          matchedClassIdList.includes(item.infectionAndIsolationSecurityClass)
      );
    },
    id() {
      return parseInt(this.$route.params.functionId, 10);
    },
    ...mapGetters({
      filterFunctionInputList: "secFunctionInput/filterByAttributeValue",
      filterOperatorList: "operator/filterByAttributeValue",
      getFunctionById: "secFunction/getById",
    }),
    ...mapState({
      functionInputList: state => state.secFunctionInput.list,
      record: state => state.secFunction.record,
      securityClassList: state => state.securityClass.list,
      templateList: state => state.template.list,
    }),
  },
  async created() {
    await Promise.all([
      this.getFunctionInputList(),
      this.getSecurityClassList(),
      this.getTemplateList(),
    ]);

    if (!this.id) {
      this.showForm = true;
    } else {
      await this.getFunction(this.id);
    }
  },
  data() {
    return {
      classType: null,
      name: null,
      point: null,
      rules: [v => !!v || "Required field"],
      showForm: false,
      showFunctionInputForm: false,
      unique: true,
      valid: false,
      validFunctionInput: false,
      value: null,
    };
  },
  methods: {
    edit() {
      this.name = `${this.record.name}`;
      this.showForm = true;
    },
    resetForm() {
      this.name = null;
      this.showForm = false;
    },
    async save() {
      const { id, name } = this;
      if (id) {
        await this.updateFunction([id, { name }]);
      } else {
        const result = await this.createFunction({
          name,
        });
        this.$router.push({
          name: "ochinUserSecurityFunctionForm",
          params: { functionId: result.id },
        });
        return;
      }
      this.resetForm();
    },
    ...mapActions({
      createFunction: "secFunction/create",
      getFunction: "secFunction/get",
      getFunctionInputList: "secFunctionInput/list",
      getSecurityClassList: "securityClass/list",
      getTemplateList: "template/list",
      updateFunction: "secFunction/update",
    }),
  },
};
</script>
